import { useCallback, useRef, useState, type ReactNode } from "react";

/**
 * Satu tooltip untuk SELURUH grafik.
 *
 * Sebelum ini setiap komponen menanganinya sendiri-sendiri: AreaChart punya kotak
 * bergaya, DayCols punya tooltip CSS, SegBar dan ProgressRow hanya memakai atribut
 * `title` bawaan browser (jeda ±1 detik, tanpa gaya, dan tak pernah muncul di
 * peranti sentuh), sementara Spark, Gauge, dan Ring tidak punya apa pun. Empat
 * perilaku berbeda pada satu halaman terbaca sebagai produk yang tidak selesai.
 *
 * Aturan yang ditegakkan di sini:
 *  - NILAI DULU, label menyusul. Pembaca sudah tahu deretnya; yang dicari angkanya.
 *  - Kunci deret berupa GARIS pendek, bukan kotak penuh — pada kerapatan tooltip,
 *    kotak berwarna adalah tinta sebobot data yang mengerjakan tugas label.
 *  - Sasaran hover lebih besar dari mark-nya.
 *  - Fokus keyboard memunculkan isi yang sama dengan hover; tooltip menambah,
 *    tidak pernah menjadi satu-satunya jalan ke angka.
 */

export interface TipIsi { judul: ReactNode; baris: Array<{ warna?: string; label: ReactNode; nilai: ReactNode }> }

export function useTip() {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; isi: TipIsi } | null>(null);

  const tampilDi = useCallback((cx: number, cy: number, isi: TipIsi) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setTip({ x: cx - r.left, y: cy - r.top, isi });
  }, []);

  const sembunyi = useCallback(() => setTip(null), []);

  /** Sebar ke elemen mark mana pun: `{...tipUntuk(isi)}`. */
  const tipUntuk = useCallback((isi: TipIsi) => ({
    onPointerMove: (e: { clientX: number; clientY: number }) => tampilDi(e.clientX, e.clientY, isi),
    onPointerLeave: sembunyi,
    // Fokus tidak membawa koordinat pointer — pakai titik tengah elemennya.
    onFocus: (e: { currentTarget: Element }) => {
      const b = e.currentTarget.getBoundingClientRect();
      tampilDi(b.left + b.width / 2, b.top, isi);
    },
    onBlur: sembunyi,
    tabIndex: 0,
  }), [tampilDi, sembunyi]);

  return { wrapRef, tip, tampilDi, sembunyi, tipUntuk };
}

export function TipBox({ tip }: { tip: { x: number; y: number; isi: TipIsi } | null }) {
  if (!tip) return null;
  return (
    <div role="tooltip" style={{
      position: "absolute", left: tip.x, top: tip.y - 12,
      transform: "translate(-50%, -100%)", pointerEvents: "none", zIndex: 40,
      background: "var(--gray-900, #10141C)", color: "#fff",
      borderRadius: "var(--radius-sm, 8px)", padding: "8px 10px",
      fontSize: "var(--text-sm, 12.5px)", lineHeight: 1.5, whiteSpace: "nowrap",
      boxShadow: "0 10px 26px -12px rgba(4,12,32,.65)", fontVariantNumeric: "tabular-nums",
      maxWidth: 280,
    }}>
      <div style={{ opacity: .72, fontSize: "var(--text-xs, 11px)", marginBottom: 3 }}>{tip.isi.judul}</div>
      {tip.isi.baris.map((b, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {b.warna && <span style={{ width: 10, height: 2, borderRadius: 1, background: b.warna, flex: "none" }} />}
          <b style={{ fontWeight: 650 }}>{b.nilai}</b>
          <span style={{ opacity: .72, overflow: "hidden", textOverflow: "ellipsis" }}>{b.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Pembungkus yang wajib dipakai bersama useTip supaya posisi tooltip relatif benar. */
export function TipWrap({ wrapRef, children, style }: { wrapRef: React.RefObject<HTMLDivElement>; children: ReactNode; style?: React.CSSProperties }) {
  return <div ref={wrapRef} style={{ position: "relative", ...style }}>{children}</div>;
}
